/**
 * Workout Summary Service
 * Aggregates all data needed for the post-workout summary modal
 */
import { supabase } from '../supabase';
import * as Sentry from '@sentry/react';
import { detectSessionPRs, getLastSimilarWorkout, getWeekStreak } from './historyService';
import { getSetVolume } from '../utils/weightUtils';
import { calculateSessionCalories } from './calorieService';
import { dayTitle } from '../utils/dayTitle';

/**
 * Main orchestrator - generates complete workout summary
 * @param {string} sessionId - Workout session ID
 * @param {string} userId - User ID
 * @returns {Object} Complete summary data
 */
export const generateWorkoutSummary = async (sessionId, userId) => {
  try {
    // Fetch all data in parallel for performance
    const [session, logs, consistency, prs, comparison, userBodyweight] = await Promise.all([
      getSessionData(sessionId),
      getSessionLogs(sessionId),
      getConsistencyData(userId),
      detectSessionPRs(sessionId, userId),
      getLastSimilarWorkout(sessionId, userId),
      getUserBodyweight(userId),
    ]);

    // Get exercise details based on logs
    const exerciseDetails = await getExerciseDetails(logs);

    // Calculate metrics with bodyweight consideration
    const calorieData = calculateSessionCalories(logs, exerciseDetails, userBodyweight, getSetVolume);
    const metrics = calculateMetrics(session, logs, exerciseDetails, userBodyweight, calorieData);

    // Extract muscle groups
    const muscleGroups = extractMuscleGroups(exerciseDetails);

    // Get workout title
    const workoutTitle = await getWorkoutTitle(session);

    // Last time against this time, on the exercises done both times (owner, 20 October 2026)
    if (comparison?.session?.id) {
      comparison.likeForLike = await compareSharedExercises(logs, comparison.session, exerciseDetails, userBodyweight);
    }

    return {
      session,
      workoutTitle,
      metrics,
      muscleGroups,
      exercises: exerciseDetails,
      streak: consistency?.currentStreak || 0,
      prs: prs || [],
      comparison: comparison || null,
    };
  } catch (error) {
    console.error('Error generating workout summary:', error);
    throw error;
  }
};

/**
 * Get session data from database
 */
const getSessionData = async (sessionId) => {
  const { data, error } = await supabase
    .from('workout_sessions')
    .select('id, started_at, completed_at, rpe, notes, day_id, program_id')
    .eq('id', sessionId)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Get all completed logs for a session
 */
const getSessionLogs = async (sessionId) => {
  const { data, error } = await supabase
    .from('workout_logs')
    .select('id, weight, reps, exercise_id, set_number, created_at, exercise_type, duration_seconds, calories')
    .eq('session_id', sessionId)
    .eq('completed', true)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * Get exercise details for all exercises in the session
 */
const getExerciseDetails = async (logs) => {
  if (!logs || logs.length === 0) return [];

  const exerciseIds = [...new Set(logs.map(log => log.exercise_id))];

  const { data, error } = await supabase
    .from('exercises')
    .select('id, name, movement_category, secondary_muscles, is_bodyweight, is_assisted, bodyweight_multiplier')
    .in('id', exerciseIds);

  if (error) throw error;
  return data || [];
};

/**
 * Get user's bodyweight
 */
const getUserBodyweight = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('bodyweight_kg')
    .eq('auth_user_id', userId) // Fixed: use auth_user_id instead of id
    .single();

  if (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getUserBodyweight', supabaseError: error, userId } });
    console.error('Error fetching user bodyweight:', error);
    return 0;
  }

  return data?.bodyweight_kg || 0;
};

/**
 * Calculate all workout metrics
 */
const calculateMetrics = (session, logs, exerciseDetails, userBodyweight, calorieData = {}) => {
  // Create a map of exercise details for quick lookup
  const exerciseMap = new Map(exerciseDetails.map(ex => [ex.id, ex]));

  // Total volume (sum of effective weight × reps)
  // Uses getSetVolume which recalculates from current bodyweight for consistency with
  // all other analytics. For bodyweight exercises this recalculates BW × multiplier
  // rather than using the pre-saved value in log.weight.
  const totalVolume = logs.reduce((sum, log) => {
    const exercise = exerciseMap.get(log.exercise_id);
    return sum + getSetVolume(log.weight, log.reps, exercise, userBodyweight);
  }, 0);

  // Total reps
  const totalReps = logs.reduce((sum, log) => sum + (log.reps || 0), 0);

  // Total sets
  const totalSets = logs.length;

  // Unique exercises
  const uniqueExerciseIds = new Set(logs.map(log => log.exercise_id));
  const exerciseCount = uniqueExerciseIds.size;

  // Duration
  const duration = calculateDuration(session, logs);

  // RPE
  const rpe = session.rpe || null;

  return {
    totalVolume: Math.round(totalVolume),
    totalReps,
    totalSets,
    exerciseCount,
    duration,
    rpe,
    estimatedCalories: calorieData.estimated || 0,
    calorieHasCardio: calorieData.hasCardio || false,
    calorieHasResistance: calorieData.hasResistance || false,
  };
};

/**
 * This session's volume against the last time the client did this workout, counting only the exercises
 * done both times. A session with exercises skipped, swapped or added used to be held against a complete
 * one and showed drops like 94% (owner, 20 October 2026). Null when fewer than half of this session's
 * exercises were also done last time, or last time has no volume to compare with.
 */
export const compareSharedExercises = async (logs, previousSession, exerciseDetails, userBodyweight) => {
  const { data: previousLogs, error } = await supabase
    .from('workout_logs')
    .select('weight, reps, exercise_id')
    .eq('session_id', previousSession.id)
    .eq('completed', true);
  if (error || !previousLogs?.length) return null;
  return likeForLike(logs, previousLogs, exerciseDetails, userBodyweight, previousSession.completed_at);
};

export const likeForLike = (logs, previousLogs, exerciseDetails, userBodyweight, previousDate = null) => {
  const exerciseMap = new Map((exerciseDetails || []).map(ex => [ex.id, ex]));
  const now = new Set((logs || []).map(l => l.exercise_id));
  const before = new Set((previousLogs || []).map(l => l.exercise_id));
  const shared = [...now].filter(id => before.has(id));
  if (shared.length === 0 || shared.length * 2 < now.size) return null;
  const volumeOf = (rows) => rows
    .filter(l => shared.includes(l.exercise_id))
    .reduce((sum, l) => sum + getSetVolume(l.weight, l.reps, exerciseMap.get(l.exercise_id), userBodyweight), 0);
  const current = Math.round(volumeOf(logs));
  const previous = Math.round(volumeOf(previousLogs));
  if (previous <= 0) return null;
  return {
    current,
    previous,
    percent: Math.round(((current - previous) / previous) * 1000) / 10,
    sharedExercises: shared.length,
    exercisesThisTime: now.size,
    previousDate,
  };
};

// Longer than this is not one sitting (a day opened in the morning and trained in the evening, sets
// logged across a day): no duration is shown rather than "9h 12m" (owner, 20 October 2026)
const LONGEST_SESSION_MS = 4 * 60 * 60000;

/**
 * Calculate workout duration.
 *
 * Prefers the span of the actual logged sets (first → last completed
 * workout_logs.created_at) over the session clock. A workout_sessions row can be
 * created hours or days before the user actually trains (open the screen to
 * preview, a stale row resumed later, an offline session id reused) — trusting
 * session.started_at then produces absurd "68h 57m" durations. The logged sets
 * are the ground truth for when training happened; the session clock is only a
 * fallback for the degenerate 0–1 log case.
 */
export const calculateDuration = (session, logs = []) => {
  const times = (logs || [])
    .map(l => new Date(l.created_at).getTime())
    .filter(t => Number.isFinite(t));

  let diffMs;
  if (times.length >= 2) {
    const first = Math.min(...times);
    // End at the "Complete Day" tap when it's sane relative to the last set
    // (captures the final rest + logging), otherwise stop at the last set.
    const lastSet = Math.max(...times);
    const completed = session?.completed_at ? new Date(session.completed_at).getTime() : NaN;
    const end = Number.isFinite(completed) && completed >= lastSet && completed - lastSet <= 30 * 60000
      ? completed
      : lastSet;
    diffMs = end - first;
  } else if (session?.started_at && session?.completed_at) {
    // The session row is created when the day is opened, not when training starts: used only when it is
    // plausibly one sitting
    diffMs = new Date(session.completed_at) - new Date(session.started_at);
  } else {
    return 'N/A';
  }

  if (!Number.isFinite(diffMs) || diffMs < 0 || diffMs > LONGEST_SESSION_MS) return 'N/A';
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins >= 180) {
    // Over 3 hours, show in hours
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  }

  const secs = Math.floor((diffMs % 60000) / 1000);
  return `${diffMins}m ${secs}s`;
};

/**
 * Extract all unique muscle groups from exercises
 */
const extractMuscleGroups = (exercises) => {
  const muscles = new Set();

  exercises.forEach(ex => {
    if (ex.movement_category) {
      muscles.add(ex.movement_category);
    }
    if (ex.secondary_muscles) {
      const secondary = ex.secondary_muscles.split(',').map(m => m.trim());
      secondary.forEach(m => muscles.add(m));
    }
  });

  return Array.from(muscles);
};

/**
 * Get workout title (day name + muscle groups)
 */
const getWorkoutTitle = async (session) => {
  if (!session.day_id) {
    return 'Workout';
  }

  const { data, error } = await supabase
    .from('program_days')
    .select('name, day_name, day_number')
    .eq('id', session.day_id)
    .single();

  if (error || !data) {
    return 'Workout';
  }

  return dayTitle(data) || `Day ${data.day_number}`;
};

/**
 * Get consistency/streak data
 */
// The same streak as Stats and its calendar (historyService.getWeekStreak)
export const getConsistencyData = async (userId) => {
  const { current } = await getWeekStreak(userId);
  return { currentStreak: current };
};
